/* 
eslint-disable no-mixed-operators
*/
import React, {Suspense} from 'react';
import dlv from 'dlv';
import mergeOptions from 'merge-options';
import useDeviceVariance from '../../../core/devicevariance';
import { isString, concatUrl, getQueryArgs } from '../../../../util';
import { useUsers } from '../../auth/users';
import useConfig from '../../../../config';
import fetchConfig from '../../../../util/config';
import { getConfigResourcePath } from '../../../../util/client';
import { useAuthData } from '../../auth';
import useScreens  from '../../';
import DynamicScreenSequence from './sequence';
import DynamicSwitchScreen from './switch'
import DynamicIfScreen from './if';
import DynamicScreen from './screen';
import ApplySkinOverride from '../../../skin-override';

function getScreenForType(descriptor) {
	if(Array.isArray(descriptor) ) return DynamicScreenSequence;
	if(descriptor.type) {
		if(descriptor.type === 'switch') return DynamicSwitchScreen;
		if(descriptor.type === 'if') return DynamicIfScreen;
	}
	return DynamicScreen;
}

const DynamicScreenSelector = React.memo(
	({children, previousScreenResults, onTemplate, activeTemplate, ...props}) => {
		const device = useDeviceVariance();
		const [descriptor, setDescriptor] = React.useState();
		const { Blank } = useScreens();
		const [{Template, ...templateData}, setTemplateData] = React.useState({Template: Blank});
		const { getSelectedUser, getLoggedInUser, stateKey } = useUsers();
		const authData = useAuthData();
		const { baseUrl, data: configData = {}, scripts: configScripts = {} }  = useConfig();

		/*
		This is where we want to inject values from the environment into the 
		dynamic screen system:

		user : If the User API gives us a selected user, we always want to inject it 
		as it will always be the data we are operating on.
		*/
		if(!previousScreenResults || !Array.isArray(previousScreenResults)) { // if falsy, this is the first screen in a top-level sequence.
			const loggedinuser = getLoggedInUser();
			const user = getSelectedUser();
			const queryArgs = getQueryArgs();
			previousScreenResults = [{ device }, { query:queryArgs, ...queryArgs }, {...previousScreenResults}, { configData }, { auth:authData },{ user, loggedinuser }];
		}

		if(descriptor && descriptor.data) {
			// Merging base `configData` with url specific `descriptor.data`.
			// This merging is required because there could be cases where data override takes place.
			const d = previousScreenResults.find(p => p.configData) || {configData:{}};
			d.configData = mergeOptions(d.configData, descriptor.data);
		}

		props = {
			previousScreenResults,
			...props,
			...descriptor && descriptor.scripts && {
				configScripts: mergeOptions(configScripts, descriptor.scripts)
			}
		};

		let screenKey=`screen-${stateKey}`

		const isTemplateRoot = !onTemplate;
		
		/*
		The screen is rendered before the template in order for the 'info' data to be extracted 
		from it's descriptor taking into account data binding local to the screen.

		Because of this, we have to hide the contents to avoid the screen being rendered 
		blank, then shifting to the template layout.

		isTemplateReady is the flag that tell us we are ready to render.
		*/
		const isTemplateReady = !!(templateData && templateData.path);

		onTemplate = !isTemplateRoot ? onTemplate : (iTemplateData) => setTemplateData(iTemplateData);

		React.useEffect(() => {
			fetchConfig(
				isString(props.descriptor) ? concatUrl(getConfigResourcePath(props.descriptor), baseUrl) : props.descriptor,
				null
			).then(d => setDescriptor(d));
		}, []); //eslint-disable-line react-hooks/exhaustive-deps

		let contents = null;
		if(descriptor) {
			const Screen = getScreenForType(descriptor);
			const screen = <Screen {...props} descriptor={descriptor} onTemplate={onTemplate} key={screenKey} activeTemplate={activeTemplate}>{children}</Screen>;

			contents = isTemplateRoot ? (
				<Template {...templateData} id={dlv(templateData, "descriptor.id")} isReady={isTemplateReady} logo={dlv(templateData, "descriptor.logo")}>
					{React.cloneElement(screen, {activeTemplate:Template})}
				</Template>
			) : screen;

			const style = dlv(descriptor, 'style');
			if (style) {
				contents = (
					<React.Fragment>
						<ApplySkinOverride style={style} />
						{contents}
					</React.Fragment>
				)
			}
		}

		return <Suspense fallback={null}>{contents}</Suspense>;
	}
);

export default DynamicScreenSelector;